import {copilotLocalStorage, type SavedMessageSource} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {useEffect, useRef} from 'react'

// Latest text of mounted drafts that flush on leave, so a composer replaced within one render
// can read it before the outgoing composer's cleanup writes it to storage.
const unsavedDrafts = new Map<string, string>()

/**
 * Saves a composer's text to local storage under `draftId`. Emptying the text
 * removes the saved draft. An undefined `draftId` skips saving.
 * `flushOnLeave` saves the latest text immediately when the draft unmounts or `draftId` changes.
 */
export function usePersistedDraft(
  draftId: string | undefined,
  source: SavedMessageSource,
  value: string,
  {flushOnLeave = false}: {flushOnLeave?: boolean} = {},
) {
  const latestRef = useRef({draftId, value})

  useEffect(() => {
    latestRef.current = {draftId, value}
    if (!draftId) return
    if (flushOnLeave) {
      if (value) unsavedDrafts.set(`${source}:${draftId}`, value)
      else unsavedDrafts.delete(`${source}:${draftId}`)
    }
    if (value) {
      copilotLocalStorage.setSavedMessage(draftId, source, value)
      return
    }

    // `setSavedMessage` waits 100ms and only runs its latest call. Calling it with
    // null cancels a pending save of the old text; the fast clear removes what is already saved.
    copilotLocalStorage.setSavedMessage(draftId, source, null)
    copilotLocalStorage.setSavedMessageFast(draftId, source, null)
  }, [draftId, source, value, flushOnLeave])

  useEffect(() => {
    if (!flushOnLeave) return
    return () => {
      // The 100ms wait is shared across all drafts, so the next draft's save would cancel this one's pending save.
      const latest = latestRef.current
      if (latest.draftId !== draftId || !draftId) return
      copilotLocalStorage.setSavedMessageFast(draftId, source, latest.value || null)
      unsavedDrafts.delete(`${source}:${draftId}`)
    }
  }, [draftId, source, flushOnLeave])
}

export function readPersistedDraft(draftId: string | undefined, source: SavedMessageSource): string | null {
  if (!draftId) return null
  return unsavedDrafts.get(`${source}:${draftId}`) ?? copilotLocalStorage.getSavedMessage(draftId, source)
}

/** Removes a saved draft, but only while it still holds `expectedText`, so newer text is kept. */
export function clearPersistedDraft(draftId: string, source: SavedMessageSource, expectedText: string) {
  if (readPersistedDraft(draftId, source) !== expectedText) return
  unsavedDrafts.delete(`${source}:${draftId}`)
  copilotLocalStorage.setSavedMessage(draftId, source, null)
  copilotLocalStorage.setSavedMessageFast(draftId, source, null)
}
